import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tooltip as ShadcnTooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { TrendingUp, FileDown, Filter, Building2, MapPin } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AREAS = [
  { key: 'Personale', color: '#3BA99C', shortKey: 'Personale' },
  { key: 'Terze Parti', color: '#60D1B4', shortKey: 'Terze Parti' },
  { key: 'Misure Organizzative', color: '#89E3FC', shortKey: 'Misure Org.' },
  { key: 'Sicurezza Fisica', color: '#5941F5', shortKey: 'Sicurezza Fis.' },
  { key: 'ICT', color: '#21358E', shortKey: 'ICT' },
  { key: 'Incidenti di Sicurezza', color: '#E57D68', shortKey: 'Incidenti' }
];

// Helper to abbreviate area names
const abbreviateAreaName = (name) => {
  const area = AREAS.find(a => a.key === name);
  return area?.shortKey || name;
};

const Statistics = () => {
  const { token, user } = useAuth();
  const [trendData, setTrendData] = useState([]);
  const [areasTrendData, setAreasTrendData] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(true);
  
  const [users, setUsers] = useState([]);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [selectedUser, setSelectedUser] = useState('all');
  const [selectedQuestionnaires, setSelectedQuestionnaires] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  
  // Admin filters for CAA and Ufficio
  const [caaList, setCaaList] = useState([]);
  const [ufficiList, setUfficiList] = useState([]);
  const [selectedCaa, setSelectedCaa] = useState('all');
  const [selectedUfficio, setSelectedUfficio] = useState('all');
  
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  useEffect(() => {
    fetchInitialData();
  }, []);

  // When CAA changes, fetch uffici for that CAA
  useEffect(() => {
    if (isAdmin && selectedCaa !== 'all') {
      fetchUfficiForCaa(selectedCaa);
    } else {
      setUfficiList([]);
      setSelectedUfficio('all');
    }
  }, [selectedCaa, isAdmin]);

  useEffect(() => {
    fetchTrendData();
  }, [startDate, endDate, selectedUser, selectedCaa, selectedUfficio]);

  // Auto-generate comparison when questionnaires are selected
  useEffect(() => {
    if (selectedQuestionnaires.length >= 2) {
      generateComparison();
    } else {
      setComparisonData(null);
    }
  }, [selectedQuestionnaires]);

  const fetchInitialData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Only fetch users list and CAA list if admin
      if (isAdmin) {
        const [usersResponse, caaResponse] = await Promise.all([
          axios.get(`${API}/users`, { headers }),
          axios.get(`${API}/caa`, { headers })
        ]);
        setUsers(usersResponse.data);
        setCaaList(caaResponse.data);
      }
      
      const qResponse = await axios.get(`${API}/questionnaires`, { headers });
      const completed = qResponse.data.filter(q => q.status === 'completed');
      // Sort by completion date (oldest first for correct trend)
      completed.sort((a, b) => new Date(a.completed_at) - new Date(b.completed_at));
      setQuestionnaires(completed);
      
    } catch (error) {
      console.error('Error fetching initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUfficiForCaa = async (caaId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/caa/${caaId}/uffici`, { headers });
      setUfficiList(response.data);
    } catch (error) {
      console.error('Error fetching uffici:', error);
      setUfficiList([]);
    }
  };

  const fetchTrendData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      let url = `${API}/analytics/trend?`;
      if (startDate) url += `start_date=${startDate}&`;
      if (endDate) url += `end_date=${endDate}&`;
      if (selectedUser !== 'all') url += `user_id=${selectedUser}&`;
      if (selectedCaa !== 'all') url += `caa_id=${selectedCaa}&`;
      if (selectedUfficio !== 'all') url += `ufficio_id=${selectedUfficio}`;
      
      const trendResponse = await axios.get(url, { headers });
      setTrendData(trendResponse.data);

      let areasUrl = `${API}/analytics/areas-trend?`;
      if (startDate) areasUrl += `start_date=${startDate}&`;
      if (endDate) areasUrl += `end_date=${endDate}&`;
      if (selectedUser !== 'all') areasUrl += `user_id=${selectedUser}&`;
      if (selectedCaa !== 'all') areasUrl += `caa_id=${selectedCaa}&`;
      if (selectedUfficio !== 'all') areasUrl += `ufficio_id=${selectedUfficio}`;
      
      const areasTrendResponse = await axios.get(areasUrl, { headers });
      setAreasTrendData(areasTrendResponse.data);
    } catch (error) {
      console.error('Error fetching trend data:', error);
      toast.error('Errore nel caricamento dei dati');
    }
  };

  const generateComparison = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(
        `${API}/analytics/compare-questionnaires`,
        selectedQuestionnaires,
        { headers }
      );
      setComparisonData(response.data);
    } catch (error) {
      console.error('Error comparing:', error);
    }
  };

  const handleExportPdf = async () => {
    if (selectedQuestionnaires.length === 0) {
      toast.error('Seleziona almeno un questionario per l\'export');
      return;
    }

    setExportingPdf(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(
        `${API}/analytics/export-comparison-pdf`,
        selectedQuestionnaires,
        { headers, responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = selectedQuestionnaires.length > 1 
        ? `Confronto_Questionari_${new Date().toISOString().split('T')[0]}.pdf`
        : `Report_${new Date().toISOString().split('T')[0]}.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('PDF scaricato con successo');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Errore nell\'export del PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const handleReset = () => {
    setStartDate('');
    setEndDate('');
    setSelectedUser('all');
    setSelectedCaa('all');
    setSelectedUfficio('all');
    setSelectedQuestionnaires([]);
    setComparisonData(null);
  };

  const filteredQuestionnaires = questionnaires.filter(q => {
    if (selectedUser !== 'all' && q.user_id !== selectedUser) return false;
    if (startDate && new Date(q.completed_at) < new Date(startDate)) return false;
    if (endDate && new Date(q.completed_at) > new Date(endDate)) return false;
    // Filter by CAA
    if (selectedCaa !== 'all' && q.caa_id !== selectedCaa) return false;
    // Filter by Ufficio
    if (selectedUfficio !== 'all') {
      const qUffici = q.uffici_caa_ids || q.uffici_caa || [];
      if (!qUffici.includes(selectedUfficio) && q.ufficio_id !== selectedUfficio) return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3BA99C]"></div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      <div>
        <h1 className="text-4xl font-bold text-slate-900" data-testid="statistics-title">
          Statistiche
        </h1>
        <p className="text-slate-500 mt-2">
          {isAdmin ? 'Analisi e confronto questionari di tutti gli utenti' : 'Analisi e confronto dei tuoi questionari'}
        </p>
      </div>

      {/* Filters Card */}
      <Card data-testid="filters-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filtri
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Prima riga: filtri base */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
            {/* Date Range */}
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Data Inizio</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-2 py-1.5 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3BA99C]"
                data-testid="start-date-input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Data Fine</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-2 py-1.5 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3BA99C]"
                data-testid="end-date-input"
              />
            </div>
            
            {/* CAA Filter - Only for admin */}
            {isAdmin && (
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1 flex items-center gap-1">
                  <Building2 className="w-3 h-3" />
                  CAA
                </label>
                <Select value={selectedCaa} onValueChange={setSelectedCaa}>
                  <SelectTrigger className="w-full bg-white border-slate-200 h-9" data-testid="caa-select">
                    <SelectValue placeholder="Tutti i CAA" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti i CAA</SelectItem>
                    {caaList.map((caa, index) => (
                      <SelectItem key={`caa-${index}`} value={caa.caa_id}>{caa.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Ufficio CAA Filter - Only for admin */}
            {isAdmin && (
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1 flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  Ufficio
                </label>
                <Select value={selectedUfficio} onValueChange={setSelectedUfficio} disabled={selectedCaa === 'all'}>
                  <SelectTrigger className="w-full bg-white border-slate-200 h-9" data-testid="ufficio-select">
                    <SelectValue placeholder={selectedCaa === 'all' ? 'Seleziona CAA' : 'Tutti'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{selectedCaa === 'all' ? 'Seleziona CAA' : 'Tutti'}</SelectItem>
                    {ufficiList.map((ufficio, index) => (
                      <SelectItem key={`ufficio-${index}`} value={ufficio.ufficio_id}>
                        {ufficio.code} - {ufficio.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* User Filter - Only for admin */}
            {isAdmin && (
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Utente</label>
                <Select value={selectedUser} onValueChange={setSelectedUser}>
                  <SelectTrigger className="w-full bg-white border-slate-200 h-9" data-testid="user-select">
                    <SelectValue placeholder="Tutti" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti</SelectItem>
                    {users.map((u, index) => (
                      <SelectItem key={`user-${index}`} value={u.user_id}>{u.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
            
          {/* Seconda riga: Selezione questionari */}
          <div className="border-t border-slate-100 pt-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-700">
                Questionari da confrontare (max 5)
              </label>
              <div className="flex items-center gap-3">
                <span className={`text-sm ${selectedQuestionnaires.length >= 5 ? 'text-orange-600 font-medium' : 'text-slate-500'}`}>
                  {selectedQuestionnaires.length}/5 selezionati
                  {selectedQuestionnaires.length >= 2 && <span className="text-[#3BA99C] ml-2">✓ Confronto attivo</span>}
                </span>
                {selectedQuestionnaires.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectedQuestionnaires([])}
                    className="text-xs text-slate-500 hover:text-slate-700 underline"
                  >
                    Deseleziona tutti
                  </button>
                )}
              </div>
            </div>
            
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              {filteredQuestionnaires.length === 0 ? (
                <p className="text-sm text-slate-500 p-4 text-center">Nessun questionario completato disponibile</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 max-h-[180px] overflow-y-auto">
                  {filteredQuestionnaires.map(q => {
                    const isSelected = selectedQuestionnaires.includes(q.questionnaire_id);
                    const isDisabled = !isSelected && selectedQuestionnaires.length >= 5;
                    
                    return (
                      <label 
                        key={q.questionnaire_id}
                        className={`flex items-start gap-2 px-3 py-2 border-b border-r border-slate-100 cursor-pointer transition-colors
                          ${isSelected ? 'bg-[#3BA99C]/10' : 'hover:bg-slate-50'}
                          ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                        data-testid={`questionnaire-checkbox-${q.questionnaire_id}`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={isDisabled}
                          onChange={() => {
                            if (isSelected) {
                              setSelectedQuestionnaires(prev => prev.filter(id => id !== q.questionnaire_id));
                            } else if (selectedQuestionnaires.length < 5) {
                              setSelectedQuestionnaires(prev => [...prev, q.questionnaire_id]);
                            }
                          }}
                          className="w-4 h-4 mt-0.5 rounded border-slate-300 text-[#3BA99C] focus:ring-[#3BA99C]"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-medium text-sm text-slate-800 truncate">{q.title}</span>
                            <span className={`px-1.5 py-0.5 text-xs font-semibold rounded
                              ${q.total_score >= 75 ? 'bg-green-100 text-green-700' : 
                                q.total_score >= 50 ? 'bg-yellow-100 text-yellow-700' : 
                                q.total_score >= 25 ? 'bg-orange-100 text-orange-700' : 
                                'bg-red-100 text-red-700'}`}>
                              {q.total_score}%
                            </span>
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5 truncate">
                            {q.user_name || 'Utente'} • {q.caa_name || 'N/D'}
                            {q.ufficio_name && (
                              <span className="text-slate-400"> • {q.ufficio_name}</span>
                            )}
                            {q.ufficio_code && (
                              <span className="text-slate-400 font-mono text-[10px]"> ({q.ufficio_code})</span>
                            )}
                          </div>
                          <div className="text-xs text-slate-400">
                            {new Date(q.completed_at).toLocaleDateString('it-IT')} ore {new Date(q.completed_at).toLocaleTimeString('it-IT', {hour: '2-digit', minute: '2-digit'})}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t border-slate-100">
            <button
              onClick={handleReset}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
              data-testid="reset-filters-btn"
            >
              Reset Filtri
            </button>
            <TooltipProvider delayDuration={200}>
              <ShadcnTooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={handleExportPdf}
                    disabled={exportingPdf || selectedQuestionnaires.length === 0}
                    className="flex items-center gap-2 px-4 py-2 bg-[#21358E] hover:bg-[#1a2a70] text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="export-pdf-btn"
                  >
                    <FileDown className="w-4 h-4" />
                    {exportingPdf ? 'Generando...' : selectedQuestionnaires.length > 1 ? 'Report Confronto PDF' : 'Report Completo PDF'}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  {selectedQuestionnaires.length > 1 
                    ? 'Report di confronto tra i questionari selezionati' 
                    : 'Report di valutazione completo con tutte le risposte'}
                </TooltipContent>
              </ShadcnTooltip>
            </TooltipProvider>
          </div>
        </CardContent>
      </Card>

      {/* Comparison Charts - Show when 2+ questionnaires selected */}
      {comparisonData && (
        <div className="space-y-8">
          {/* Graduatoria Punteggi - Horizontal Bar Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Graduatoria Punteggi</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[...comparisonData.questionnaires]
                  .sort((a, b) => b.total_score - a.total_score)
                  .map((q, idx) => (
                    <div key={q.questionnaire_id} className="flex items-center gap-4">
                      <div className="w-48 text-sm font-medium text-slate-700 truncate" title={q.display_name}>
                        {q.display_name}
                      </div>
                      <div className="flex-1 h-8 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all duration-500 flex items-center justify-end pr-3"
                          style={{ 
                            width: `${q.total_score}%`, 
                            backgroundColor: ['#3BA99C', '#60D1B4', '#5941F5', '#21358E', '#E57D68'][idx % 5] 
                          }}
                        >
                          <span className="text-white text-sm font-bold">{q.total_score}%</span>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>

          {/* Confronto per Area - Barre Orizzontali con Tooltip */}
          <Card>
            <CardHeader>
              <CardTitle>Confronto per Area</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-5">
                {AREAS.map((area) => {
                  const areaData = comparisonData.comparison_by_area.find(d => d.area === area.key);
                  return (
                    <div key={area.key} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-slate-700">{area.key}</span>
                      </div>
                      <div className="space-y-1.5">
                        {comparisonData.questionnaires.map((q, idx) => {
                          const score = areaData?.[q.display_name] || 0;
                          const color = ['#3BA99C', '#60D1B4', '#5941F5', '#21358E', '#E57D68'][idx % 5];
                          return (
                            <div 
                              key={q.questionnaire_id} 
                              className="flex items-center gap-3 group relative"
                            >
                              <div className="w-32 text-xs text-slate-500 truncate font-medium" title={q.display_name}>
                                {q.display_name}
                              </div>
                              <div className="flex-1 h-6 bg-slate-100 rounded-full relative">
                                <div 
                                  className="h-full rounded-full transition-all duration-500 flex items-center justify-end pr-2 cursor-pointer"
                                  style={{ 
                                    width: `${Math.max(score, 5)}%`, 
                                    backgroundColor: color
                                  }}
                                  title={`${q.display_name} - ${area.key}: ${score}%`}
                                >
                                  <span className="text-xs font-bold text-white drop-shadow">{score}%</span>
                                </div>
                              </div>
                              {/* Tooltip on hover */}
                              <div className="absolute left-1/2 -translate-x-1/2 -top-7 bg-slate-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50 shadow-lg">
                                {q.display_name}: {score}%
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Legenda questionari */}
              <div className="flex flex-wrap justify-center gap-4 mt-6 pt-4 border-t border-slate-100">
                {comparisonData.questionnaires.map((q, idx) => (
                  <div key={q.questionnaire_id} className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: ['#3BA99C', '#60D1B4', '#5941F5', '#21358E', '#E57D68'][idx % 5] }}
                    />
                    <span className="text-xs text-slate-600 font-medium">{q.display_name}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Trend Charts */}
      <Card data-testid="trend-chart-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Trend Punteggio Totale
          </CardTitle>
        </CardHeader>
        <CardContent>
          {trendData.length === 0 ? (
            <p className="text-center text-slate-500 py-8">Nessun dato disponibile per i filtri selezionati</p>
          ) : (
            <div className="flex flex-col items-center overflow-x-auto">
              <LineChart 
                width={900}
                height={350}
                data={trendData} 
                margin={{ top: 20, right: 40, left: 40, bottom: 40 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis 
                  dataKey="date" 
                  stroke="#64748B"
                  fontSize={12}
                  angle={-30}
                  textAnchor="end"
                  height={50}
                />
                <YAxis 
                  stroke="#64748B" 
                  domain={[0, 100]}
                  fontSize={12}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip 
                  formatter={(value) => [`${value}%`, 'Punteggio']}
                  contentStyle={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="score" 
                  stroke="#3BA99C" 
                  strokeWidth={3}
                  dot={{ fill: '#3BA99C', r: 5, strokeWidth: 2, stroke: '#fff' }}
                  activeDot={{ r: 7, fill: '#3BA99C', stroke: '#fff', strokeWidth: 2 }}
                  name="Punteggio (%)"
                />
              </LineChart>
              <div className="mt-4 flex items-center gap-2">
                <div className="w-4 h-3 rounded bg-[#3BA99C]"></div>
                <span className="text-sm text-slate-600">Punteggio (%)</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card data-testid="areas-comparison-card">
        <CardHeader>
          <CardTitle>Confronto Tutte le Aree nel Tempo</CardTitle>
        </CardHeader>
        <CardContent>
          {areasTrendData.length === 0 ? (
            <p className="text-center text-slate-500 py-8">Nessun dato disponibile per i filtri selezionati</p>
          ) : (
            <div className="flex flex-col items-center overflow-x-auto">
              <LineChart 
                width={900}
                height={400}
                data={areasTrendData} 
                margin={{ top: 20, right: 40, left: 40, bottom: 40 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis 
                  dataKey="date" 
                  stroke="#64748B" 
                  fontSize={12}
                  angle={-30}
                  textAnchor="end"
                  height={50}
                />
                <YAxis 
                  stroke="#64748B" 
                  domain={[0, 100]} 
                  fontSize={12}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip 
                  formatter={(value, name) => [`${value}%`, abbreviateAreaName(name)]}
                  contentStyle={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                {AREAS.map(area => (
                  <Line
                    key={area.key}
                    type="monotone"
                    dataKey={area.key}
                    stroke={area.color}
                    strokeWidth={2}
                    dot={{ r: 4, fill: area.color, strokeWidth: 2, stroke: '#fff' }}
                    activeDot={{ r: 6, fill: area.color, stroke: '#fff', strokeWidth: 2 }}
                    name={area.key}
                  />
                ))}
              </LineChart>
              {/* Custom legend below the chart with more spacing */}
              <div className="mt-6 flex flex-wrap justify-center gap-x-6 gap-y-3">
                {AREAS.map(area => (
                  <div key={area.key} className="flex items-center gap-2">
                    <div 
                      className="w-4 h-3 rounded" 
                      style={{ backgroundColor: area.color }}
                    />
                    <span className="text-sm text-slate-600">{area.shortKey}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Statistics;
